// Imports
import React, { useEffect, useMemo, useState } from "react";

// Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { Divider, Grid } from "@mui/material";

// Controls
import {
    Button,
    Form,
    Input,
    Modal,
    TextArea,
    Switch,
    Select
} from "@gpstrackit/gtc-react-controls";

// Helpers
import { getCountryCodes } from "Core/data/Helpers";

// Components
import { NotificationManager, NotificationContainer } from "Components/Notifications";
import InputSearchMap from 'Components/Map/components/InputSearchMap';
import TagsManage from 'Modules/.common/components/TagsManage';

// Actions
import { setFieldIdCustomerSelector } from 'Redux/actions'
import {
    workOrdersGetCustomer,
    workOrderCreateCustomer,
    workOrderUpdateCustomer,
    reset,
} from "./redux/slice";

// Styles
import './index.scss'

const INIT_DATA = {
    name: null,
    ein: null,
    contact_id: null,
    contact_name: null,
    contact_phone: null,
    contact_email: null,
    location_id: null,
    location_name: null,
    location_address: [],
    location_contact: null,
    notifyByEmail: false,
    notifyByPhone: false,
    tags: []
};

const CreateEditCustomer = ({ refresh }) => {

    //States
    const [formData, setFormData] = useState(INIT_DATA);
    const [changes, setChanges] = useState({});
    const [errorAddress, setErrorAddress] = useState(null);
    const [errorTags, setErrorTags] = useState(null);

    const { messages, locale } = useIntl();

    //Form refs
    const createEditWorkOrderFormRef = Form.useForm();

    //Redux
    const {
        loading,
        open,
        create,
        error,
        customer,
        customerId,
        update
    } = useSelector((state) => state.workOrdersCreateEditCustomerRedux);

    const dispatch = useDispatch();

    //Other hooks
    const title = useMemo(() => {
        return customerId ? `${messages['workOrder_createEditCustomer_editTitle']} ${customer ? `"${customer?.name}"` : ""}` : messages['workOrder_createEditCustomer_createTitle']
    }, [locale, customerId, customer]);

    //Handlers and utils
    const handleClose = () => {
        setFormData(INIT_DATA);
        dispatch(reset());
    };

    const handleSuccess = () => {
        NotificationManager.success(
            messages['workOrder_createEditCustomer_customerSaved'],
            messages["success"],
            3000,
            null,
            null,
            "success"
        );
        refresh && refresh();
        handleClose();
    };

    const handleCreateEditCustomer = () => {

        if (errorAddress) return
        const address = formData?.location_address?.[0] || {};

        const isEditing = !!customerId;
        let payload = {};
        if (isEditing) {
            payload.id = customerId
            if (changes.name) payload.name = formData?.name;
            if (changes.ein) payload.ein = formData?.ein;

            if (changes.contact_email || changes.contact_name || changes.notifyByPhone || changes.notifyByEmail || changes.contact_codeCountry || changes.contact_phone) {
                payload.customerContact = {
                    id: formData?.contact_id,
                    ...(changes.contact_email && { email: formData?.contact_email }),
                    ...(changes.contact_name && { name: formData?.contact_name }),
                    ...((changes.contact_codeCountry || changes.contact_phone) && { phone: formData?.contact_phone, countryCode: formData?.contact_codeCountry?.code }),
                    ...(changes.notifyByPhone && { notifyByPhone: formData?.notifyByPhone, phone: formData?.contact_phone, countryCode: formData?.contact_codeCountry?.code }),
                    ...(changes.notifyByEmail && { notifyByEmail: formData?.notifyByEmail, email: formData?.contact_email }),
                };
            }

            if (changes.tags) {
                payload.tagIds = formData?.tags?.map((item) => item?.id) || [];
            }

            if (changes.location_name || changes.location_address || changes.location_contact) {
                payload.customerLocation = {
                    id: formData?.location_id,
                    ...(changes.location_name && { name: formData?.location_name }),
                    ...(changes.location_address && address.fulladdress && {
                        address: {
                            address: address.fulladdress,
                            city: address.city,
                            formatted: address.fulladdress,
                            state: address.state,
                            zip: address.postalcode,
                        },
                        latitude: address.lat,
                        longitude: address.lng,
                    }),
                    ...(changes.location_contact && { contact: formData?.location_contact }),
                };
            }
        } else {
            payload = {
                name: formData?.name,
                ein: formData?.ein,
                customerLocation: {
                    name: formData?.location_name,
                    locationType: 'main',
                    address: address.fulladdress
                        ? {
                            address: address.fulladdress,
                            city: address.city,
                            formatted: address.fulladdress,
                            state: address.state,
                            zip: address.postalcode,
                        }
                        : undefined,
                    latitude: address.lat || undefined,
                    longitude: address.lng || undefined,
                    contact: formData?.location_contact,
                },
                customerContact: {
                    name: formData?.contact_name,
                    email: formData?.contact_email,
                    phone: formData?.contact_phone,
                    countryCode: formData?.contact_codeCountry?.code,
                    notifyByPhone: formData?.notifyByPhone,
                    notifyByEmail: formData?.notifyByEmail,
                },
                tagIds: formData?.tags?.map((item) => item?.id) || [],
            };
        }

        if (!isEditing) {
            dispatch(workOrderCreateCustomer(payload));
        } else {
            dispatch(workOrderUpdateCustomer(payload));
        }
    }

    useEffect(() => {
        if (create || update) {
            dispatch(setFieldIdCustomerSelector({ value: create?.id, field: 'customerId' }))
            handleSuccess()
        }
    }, [create, update])

    useEffect(() => {
        if (error) {
            let errorMessage = "";
            const errorLower = error?.error?.toLowerCase();

            const errorMessages = {
                "duplicated name": messages['workOrder_createEditCustomer_customerNameIsAlreadyInUse'],
                "duplicated ein": messages['workOrder_createEditCustomer_customerEinIsAlreadyInUse'],
                "duplicated contact name": messages['workOrder_createEditCustomer_contactNameIsAlreadyInUse'],
                "duplicated contact phone": messages['workOrder_createEditCustomer_contactPhoneIsAlreadyInUse'],
                "duplicated contact email": messages['workOrder_createEditCustomer_contactEmailIsAlreadyInUse'],
                "duplicated location name": messages['workOrder_createEditCustomer_locationNameIsAlreadyInUse'],
            };

            for (const key in errorMessages) {
                if (errorLower?.includes(key)) {
                    errorMessage = errorMessages[key];
                    break;
                }
            }

            NotificationManager.error(
                errorMessage || messages['errorMessage'],
                messages["error"],
                3000,
            );

            dispatch(reset({ error: null }));
        }
    }, [error])

    useEffect(() => {
        if (open && customerId) {
            dispatch(workOrdersGetCustomer({ id: customerId }))
        }
    }, [open])


    useEffect(() => {
        if (customer) {
            const countryCode = getCountryCodes()
            let currentCountryCode = countryCode.find((item) => item?.code === customer?.customerContact?.countryCode)

            let filteredTags = []

            if (customer?.tagIds.length && (customer?.tagIds.includes(-99) || customer?.tagIds.includes("-99"))) {
                filteredTags = [{ id: -99 }]
            } else {
                filteredTags = customer?.tags?.filter(tag => customer?.tagIds?.includes(tag.id)) || [];
            }

            let currentLocation = JSON.parse(customer?.customerLocation?.address)
            let location = {
                fulladdress: currentLocation?.formatted,
                address1: currentLocation?.address,
                city: currentLocation?.city,
                state: currentLocation?.state,
                postalcode: currentLocation?.zip,
                lat: customer?.customerLocation?.latitude,
                lng: customer?.customerLocation?.longitude,
                radius: 0,
                index: 0
            }
            let data = {
                name: customer?.name,
                ein: customer?.ein,
                contact_id: customer?.customerContact?.id,
                contact_name: customer?.customerContact?.name,
                contact_phone: customer?.customerContact?.phone,
                contact_email: customer?.customerContact?.email,
                contact_codeCountry: { ...currentCountryCode, label: currentCountryCode?.code ? `${currentCountryCode?.code} (+${currentCountryCode.id})` : "US" },
                notifyByEmail: customer?.customerContact?.notifyByEmail,
                notifyByPhone: customer?.customerContact?.notifyByPhone,
                location_id: customer?.customerLocation?.id,
                location_name: customer?.customerLocation?.name,
                location_address: [location],
                location_contact: customer?.customerLocation?.contact,
                tags: filteredTags
            }
            setFormData({ ...formData, ...data })
            createEditWorkOrderFormRef?.setValues({ ...data });
        }
    }, [customer])

    return (
        <Modal
            title={title && title.length > 48 ? title.substring(0, 45) + '..."' : title}
            open={open}
            className="modal-create-customer"
            handleClose={handleClose}
            position="right"
            style={{ zIndex: "auto" }}
            size="small"
            loading={loading}
            footer={
                <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Button
                        isDisabled={loading}
                        style={{ marginRight: 8 }}
                        onClick={handleClose}
                        variant="secondary"
                    >
                        {messages["cancel"]}
                    </Button>
                    <Button
                        isDisabled={loading}
                        onClick={() => {

                            createEditWorkOrderFormRef?.submit()

                        }}
                        variant="primary"
                    >
                        {messages["save"]}
                    </Button>
                </div>
            }
        >
            <div style={{ padding: 24 }}>
                <Form
                    ref={createEditWorkOrderFormRef.ref}
                    loading={loading}
                    onValidSubmit={(_values) => {
                        let isValid = true;
                        const address = formData?.location_address?.[0] || {};
                        if (!formData.location_address.length || (address && !address?.state)) {
                            if (!formData.location_address.length) {
                                setErrorAddress('required_field')
                            } else if (address && !address?.state) {
                                setErrorAddress('workOrder_createEditCustomer_addressFormarError')
                            }

                            isValid = false;
                        }

                        if (!formData.tags.length) {
                            setErrorTags('required_field');
                            isValid = false;
                        }

                        if (isValid) {
                            handleCreateEditCustomer();
                        }
                    }}
                    onSubmit={(_values) => {
                        let isValid = true;
                        const address = formData?.location_address?.[0] || {};
                        if (!formData.location_address.length || (address && !address?.state)) {
                            if (!formData.location_address.length) {
                                setErrorAddress('required_field')
                            } else if (address && !address?.state) {
                                setErrorAddress('workOrder_createEditCustomer_addressFormarError')
                            }
                            isValid = false;
                        }

                        if (!formData.tags.length) {
                            setErrorTags('required_field');
                            isValid = false;
                        }

                        if (isValid) {
                            setErrorAddress(null);
                            setErrorTags(null);
                        }
                    }}
                >
                    <Form.Item
                        name="name"
                        label={messages['workOrder_createEditCustomer_name']}
                        rules={[{ required: true, message: 'required_field' }]}
                    >
                        <Input
                            id="name"
                            type="text"
                            placeholder={messages['workOrder_createEditCustomer_name']}
                            onChange={(value) => {
                                setFormData({ ...formData, name: value })
                                setChanges({ ...changes, name: true })
                            }}
                            maxLength={50}
                        />
                    </Form.Item>
                    <Form.Item
                        name="ein"
                        label={messages['workOrder_createEditCustomer_ein']}
                    >
                        <Input
                            id="ein"
                            type="text"
                            placeholder={messages['workOrder_createEditCustomer_ein']}
                            onChange={(value) => {
                                setFormData({ ...formData, ein: value })
                                setChanges({ ...changes, ein: true })
                            }}
                            maxLength={50}
                        />
                    </Form.Item>
                    <div style={{ marginBottom: 10 }}>
                        <label style={{ fontWeight: 'bold' }}>{messages['workOrder_createEditCustomer_mainContact']}</label>
                        <Divider />
                    </div>
                    <Form.Item
                        name="contact_name"
                        label={messages['workOrder_createEditCustomer_name']}
                        rules={[{ required: true, message: 'required_field' }]}
                    >
                        <Input
                            id="contact_name"
                            type="text"
                            placeholder={messages['workOrder_createEditCustomer_name']}
                            onChange={(value) => {
                                setFormData({ ...formData, contact_name: value })
                                setChanges({ ...changes, contact_name: true })
                            }}
                            maxLength={50}
                        />
                    </Form.Item>
                    <Grid container spacing={1}>
                        <Grid item xs={5}>
                            <Form.Item
                                name="contact_codeCountry"
                                label={messages['workOrder_createEditCustomer_codeCountry']}
                                rules={[{ required: true, message: 'required_field' }]}
                            >
                                <Select
                                    id="contact_codeCountry"
                                    options={getCountryCodes().map((item) => ({ ...item, label: `${item.code} (+${item.id})` })) || []}
                                    placeholder={messages['workOrder_createEditCustomer_codeCountry']}
                                    valueField="id"
                                    textField="label"
                                    style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                                    isSearchable
                                    onChange={(value) => {
                                        setFormData({ ...formData, contact_codeCountry: value })
                                        setChanges({ ...changes, contact_codeCountry: true })
                                    }}
                                    className="customer-container-code-country-selector"
                                />
                            </Form.Item>
                        </Grid>
                        <Grid item xs={7}>
                            <Form.Item
                                name="contact_phone"
                                label={messages['workOrder_createEditCustomer_phone']}
                                rules={[{ required: true, message: 'required_field' }, { phone: true }]}
                            >
                                <Input
                                    id="contact_phone"
                                    type="phone"
                                    maxLength={10}
                                    minLength={10}
                                    placeholder={messages['workOrder_createEditCustomer_phone']}
                                    onChange={(value) => {
                                        setFormData({ ...formData, contact_phone: value })
                                        setChanges({ ...changes, contact_phone: true })
                                    }}
                                />
                            </Form.Item>
                        </Grid>
                    </Grid>
                    <Form.Item
                        name="contact_email"
                        label={messages['workOrder_createEditCustomer_email']}
                        rules={[
                            { required: true, message: 'required_field' },
                            { email: true }
                        ]}
                    >
                        <Input
                            id="contact_email"
                            type="email"
                            placeholder={messages['workOrder_createEditCustomer_email']}
                            onChange={(value) => {
                                setFormData({ ...formData, contact_email: value })
                                setChanges({ ...changes, contact_email: true })
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="notifyByPhone"
                        label={messages['workOrder_createEditCustomer_notifyByPhone']}
                    >
                        <Switch
                            id="notifyByPhone"
                            optionLabels={["", ""]}
                            size={"small"}
                            onChange={(value) => {
                                setFormData({ ...formData, notifyByPhone: value })
                                setChanges({ ...changes, notifyByPhone: true })
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="notifyByEmail"
                        label={messages['workOrder_createEditCustomer_notifyByEmail']}
                    >
                        <Switch
                            id="notifyByEmail"
                            optionLabels={["", ""]}
                            size={"small"}
                            onChange={(value) => {
                                setFormData({ ...formData, notifyByEmail: value })
                                setChanges({ ...changes, notifyByEmail: true })
                            }}
                        />
                    </Form.Item>
                    <div style={{ marginBottom: 10 }}>
                        <label style={{ fontWeight: 'bold' }}>{messages['workOrder_createEditCustomer_mainLocation']}</label>
                        <Divider />
                    </div>
                    <Form.Item
                        name="location_name"
                        label={messages['workOrder_createEditCustomer_name']}
                        rules={[{ required: true, message: 'required_field' }]}
                    >
                        <Input
                            id="location_name"
                            type="text"
                            placeholder={messages['workOrder_createEditCustomer_name']}
                            onChange={(value) => {
                                setFormData({ ...formData, location_name: value })
                                setChanges({ ...changes, location_name: true })
                            }}
                            maxLength={50}
                        />
                    </Form.Item>
                    <div className={errorAddress ? "customer-container-address-input-error" : ""} style={{ marginBottom: 10 }}>
                        <div style={{ marginBottom: 5 }}>
                            <span className="required-gtc-rc">*</span>
                            <label className="label-gtc-rc">{messages['workOrder_createEditCustomer_address']}</label>
                        </div>
                        {
                            !loading && (
                                <>
                                    <InputSearchMap
                                        disa
                                        normalView
                                        noMap
                                        onGetAddress={(value) => {
                                            setErrorAddress(null)
                                            setFormData({ ...formData, location_address: value })
                                            setChanges({ ...changes, location_address: true })
                                        }}
                                        onClear={() => {
                                            setFormData({ ...formData, location_address: [] })
                                            setChanges({ ...changes, location_address: true })
                                        }}
                                        onClearSetAddress={() => {
                                        }}
                                        onClearInput={() => {
                                        }}
                                        draggable={false}
                                        noOptionsText={messages['noOptions']}
                                        placeholder={messages['search']}
                                        addressValue={formData?.location_address[0]?.fulladdress}
                                    />
                                    {
                                        errorAddress &&
                                        <label className="assistive-gtc-rc-text assistive-gtc-rc-text-error" style={{ marginTop: 5 }}>{messages[errorAddress]}</label>
                                    }
                                </>
                            )
                        }
                    </div>
                    <Form.Item
                        name="location_contact"
                        label={messages['workOrder_createEditCustomer_contact']}
                    >
                        <TextArea
                            id="location_contact"
                            maxLength={500}
                            onChange={(value) => {
                                setFormData({ ...formData, location_contact: value })
                                setChanges({ ...changes, location_contact: true })
                            }}
                        />
                    </Form.Item>

                    <div className={errorTags ? "customer-container-address-input-error-tags" : ""} style={{ marginBottom: 10 }}>
                        <div style={{ marginBottom: 5 }}>
                            <span className="required-gtc-rc">*</span>
                            <label className="label-gtc-rc">{messages['workOrder_createEditCustomer_tags']}</label>
                        </div>
                        <div>
                            <TagsManage
                                onlyTags={true}
                                enableSelectAll={false}
                                disableUntaggedSelection={true}
                                mode="selector"
                                placeholder={messages["workOrder_settingsTagsSearch"]}
                                value={formData?.tags || []}
                                onChange={(values) => {
                                    setErrorTags(null);
                                    setFormData({ ...formData, tags: values })
                                    createEditWorkOrderFormRef?.setValues({ tags: values });
                                    setChanges({ ...changes, tags: true })
                                }}
                                isVisiblePill={true}
                                menuStyle={{
                                    width: "min(320px, calc(100vw - 64px))",
                                    maxWidth: "100%",
                                    maxHeight: "280px",
                                    overflowY: "auto",
                                }}
                            />
                            {
                                errorTags &&
                                <label className="assistive-gtc-rc-text assistive-gtc-rc-text-error" style={{ marginTop: 5 }}>{messages[errorTags]}</label>
                            }
                        </div>
                    </div>
                </Form>
            </div>
            <NotificationContainer />
        </Modal>
    );
};

export default CreateEditCustomer;